import { observable, makeObservable, action, runInAction } from "mobx";
import { GET_COOWNER_UNREAD_FILES_TOTAL, GET_COOWNER_UNREAD_FILES_TOTAL_IT2 } from "src/api/documents";
import {
    GetCoownerUnreadFilesTotal,
    GetCoownerUnreadFilesTotalVariables
} from "src/api/generated/GetCoownerUnreadFilesTotal";
import { GetManagerFilesByRealEstateId_realestates_files as file_item } from "src/api/generated/GetManagerFilesByRealEstateId";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import {
    GetCoownerUnreadFilesTotalIT2,
    GetCoownerUnreadFilesTotalIT2Variables,
    GetCoownerUnreadFilesTotalIT2_realestates_unread
} from "src/api/generated/GetCoownerUnreadFilesTotalIT2";
import { getLang } from "src/utils/language";

export class DocumentStore {
    rootStore: RootStore;
    displayedDocuments: file_item[] = [];
    uniqueYearsArray: string[] = [];
    selectedYears: string[] = [];
    isDocumentFilterModalOpen: boolean = false;
    coownerUnreadFilesTotal: number = 0;
    focusedFileCategoryKey?: number = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            displayedDocuments: observable,
            uniqueYearsArray: observable,
            selectedYears: observable,
            isDocumentFilterModalOpen: observable,
            coownerUnreadFilesTotal: observable,
            focusedFileCategoryKey: observable,

            setDisplayedDocuments: action,
            setUniqueYearsArray: action,
            setSelectedYears: action,
            setIsDocumentFilterModalOpen: action,
            setCoownerUnreadFilesTotal: action,
            setFocusedFileCategoryKey: action,

            loadCoownerUnreadFilesTotals: action,
            loadCoownerUnreadFilesTotalsIT2: action,
            calcUnreadFilesTotalIT2: action
        });
    }

    setDisplayedDocuments = (displayedDocuments: file_item[]) => {
        this.displayedDocuments = displayedDocuments;
    };

    setUniqueYearsArray = (uniqueYearsArray: string[]) => {
        this.uniqueYearsArray = uniqueYearsArray;
    };

    setSelectedYears = (selectedYears: string[]) => {
        this.selectedYears = selectedYears;
    };

    setIsDocumentFilterModalOpen = (isDocumentFilterModalOpen: boolean) => {
        this.isDocumentFilterModalOpen = isDocumentFilterModalOpen;
    };

    setCoownerUnreadFilesTotal = (coownerUnreadFilesTotal: number) => {
        this.coownerUnreadFilesTotal = coownerUnreadFilesTotal;
    };

    setFocusedFileCategoryKey = (focusedFileCategoryKey: number) => {
        this.focusedFileCategoryKey = focusedFileCategoryKey;
    };

    getYearsFromArray = (files: file_item[]) => {
        const newYearsArray: string[] = [];
        files.forEach((file) => {
            const year = new Date(file.filedate).getFullYear().toString();
            if (!newYearsArray.includes(year)) {
                newYearsArray.push(year);
            }
        });
        this.setUniqueYearsArray(newYearsArray);
        return newYearsArray;
    };

    loadCoownerUnreadFilesTotals = async (realestateId: string) => {
        try {
            const { data }: { data: GetCoownerUnreadFilesTotal } = await apolloClientInstance.query<
                GetCoownerUnreadFilesTotal,
                GetCoownerUnreadFilesTotalVariables
            >({
                query: GET_COOWNER_UNREAD_FILES_TOTAL,
                fetchPolicy: "no-cache",
                variables: {
                    realestateid: realestateId,
                    lang: getLang()
                }
            });

            const total = (data.unread ?? []).reduce((sum, row) => sum + (Number(row?.unviewed) || 0), 0);

            runInAction(() => {
                this.setCoownerUnreadFilesTotal(total);
            });
        } catch (error) {
            console.error("Failed to fetch unread files Total (non-IT2):", error);
            throw error;
        }
    };

    calcUnreadFilesTotalIT2 = (realestates: GetCoownerUnreadFilesTotalIT2["realestates"] = []) => {
        const unreadByFileId = new Map<string, number>();

        for (const realestate of realestates) {
            const unreadList: GetCoownerUnreadFilesTotalIT2_realestates_unread[] = realestate?.unread ?? [];

            for (const entry of unreadList) {
                const fileId = entry?.id as string | null;
                if (!fileId) continue;

                const unviewed = Number(entry?.unviewed) || 1;
                const current = unreadByFileId.get(fileId) ?? 0;

                unreadByFileId.set(fileId, Math.max(current, unviewed));
            }
        }

        const totalUnread = Array.from(unreadByFileId.values()).reduce((sum, n) => sum + n, 0);
        this.setCoownerUnreadFilesTotal(totalUnread);
    };

    loadCoownerUnreadFilesTotalsIT2 = async (realestateId: string) => {
        try {
            const { data }: { data: GetCoownerUnreadFilesTotalIT2 } = await apolloClientInstance.query<
                GetCoownerUnreadFilesTotalIT2,
                GetCoownerUnreadFilesTotalIT2Variables
            >({
                query: GET_COOWNER_UNREAD_FILES_TOTAL_IT2,
                fetchPolicy: "no-cache",
                variables: {
                    realestateid: realestateId,
                    lang: getLang()
                }
            });

            runInAction(() => {
                this.calcUnreadFilesTotalIT2(data?.realestates ?? []);
            });
        } catch (error) {
            console.error("Failed to fetch unread files Total (IT2):", error);
            throw error;
        }
    };
}
